import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { Ecran } from '../../api/contrat.js';
import { creerClient } from '../../api/client.js';
import { modeMocks } from '../../app/entree.js';
import { useRoleCourant } from '../../app/role-courant.js';
import { styleBouton } from '../../app/styles.js';
import { creerFetchMocke, sessionDepuisAdresse } from '../../mocks/fetch-mocke.js';
import { CATALOGUE_DEMARRAGE } from './catalogue.js';
import { creerControleurDemarrage } from './controleur.js';
import { Cadre, EnteteDossier, VueDemarrage } from './vues.js';

/**
 * Conteneur de D17 « Démarrage guidé » (lot L64) : le dossier vient de `?dossier=` (comme les écrans voisins du dossier), le rôle de
 * la session vient de la coquille. Le texte des étapes est la donnée versionnée du catalogue, jamais du code ; l'avancement est lu et
 * écrit par les routes `demarrage.*` du contrat.
 */
const recherche = (): string => globalThis.location?.search ?? '';
const dossierDeLAdresse = (): string | null => new URLSearchParams(recherche()).get('dossier')?.trim() || null;
const depuisLeGuide = (): boolean => new URLSearchParams(recherche()).get('guide') === '1';

function clientDeLEcran() {
  // Comme D04c : `?role=` ne vaut qu'en développement avec les mocks (modeMocks), jamais en production.
  const session = modeMocks(import.meta.env) ? sessionDepuisAdresse(recherche()) : null;
  return creerClient({ fetch: session ? creerFetchMocke({ session }) : (...args) => globalThis.fetch(...args) });
}

function Demarrage({ ecran, dossierId }: { ecran: Ecran; dossierId: string }) {
  const role = useRoleCourant() ?? ecran.role;
  const controleur = useMemo(
    () => creerControleurDemarrage({ client: clientDeLEcran(), dossierId, versionCatalogue: CATALOGUE_DEMARRAGE.version }),
    [dossierId],
  );
  const etat = useSyncExternalStore(controleur.subscribe, controleur.getEtat, controleur.getEtat);
  useEffect(() => void controleur.charger(), [controleur]);

  if (etat.statut === 'erreur') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
        <p role="alert" style={{ margin: 0 }}>
          {etat.erreur}
        </p>
        <button type="button" style={{ ...styleBouton, minHeight: 48, fontSize: 16 }} onClick={() => void controleur.reessayer()}>
          Réessayer
        </button>
      </div>
    );
  }
  return (
    <>
      <EnteteDossier dossier={etat.dossier} />
      {etat.statut === 'chargement' ? (
        <p data-chargement="" role="status" style={{ margin: 0 }}>
          Chargement de l’avancement du dossier…
        </p>
      ) : null}
      <VueDemarrage
        catalogue={CATALOGUE_DEMARRAGE}
        avancement={etat.avancement}
        role={role}
        dossierId={dossierId}
        occupe={etat.statut === 'chargement' || etat.enCours !== null}
        erreurEcriture={etat.erreurEcriture}
        mandatRechercheSigne={etat.mandatRechercheSigne}
        mandatRechercheSigneLe={etat.mandatRechercheSigneLe}
        forcages={etat.forcages}
        personnesArbre={etat.personnesArbre}
        defuntsArbre={etat.defuntsArbre}
        guide={depuisLeGuide()}
        actions={controleur}
      />
    </>
  );
}

export function EcranDemarrage({ ecran }: { ecran: Ecran }) {
  const dossierId = dossierDeLAdresse();
  return (
    <Cadre id={ecran.id} titre={ecran.titre}>
      {dossierId ? (
        <Demarrage ecran={ecran} dossierId={dossierId} />
      ) : (
        <p style={{ margin: 0 }}>Aucun dossier n’est ouvert : choisissez un dossier pour suivre son démarrage pas à pas.</p>
      )}
    </Cadre>
  );
}
